import { yupResolver } from "@hookform/resolvers/yup"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Upload, X } from "lucide-react"
import { useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

import { MessageError } from "@/components/shared"
import { BottomSheetModal } from "@/components/shared/ui/bottom-sheet-modal"
import { Alert, AlertDescription, Button, Textarea } from "@/components/ui"
import { getLinkS3UploadDraft, updateDraft } from "@/features/jobs/manage/api/draft.api"
import { EFolderAssets, EMediaType } from "@/modules/helpers/enum"
import { useCurrentUser } from "@/modules/helpers/hooks/shared"
import { cn, generateUniqueId } from "@/modules/helpers/hooks/shared/utils"
import { schemaCommentDraft } from "@/modules/helpers/schemas"
import {
  getImageByNameFromS3,
  sanitizeFileName,
  uploadMultipleFilesToS3
} from "@/modules/helpers/utils"
import { Draft, DraftGallery, Potential } from "@/modules/types"

interface DraftForm {
  description: string
  sources: { file: File; id: string }[]
  error?: string
}

interface UpdateDraftProps {
  potential?: Partial<Potential>
  disabled?: boolean
  draft?: Draft
  refetch?: () => void
}

export const UpdateDraft: React.FC<UpdateDraftProps> = ({ draft, refetch }) => {
  const queryClient = useQueryClient()
  const { user } = useCurrentUser()
  const [openUpload, setOpenUpload] = useState(false)
  const [progress, setProgress] = useState(0)
  const [openConfirm, setOpenConfirm] = useState(false)
  const [sourceExited, setSourceExited] = useState<DraftGallery[]>([])

  const refDeleted = useRef<string[]>([])
  const sourceRef = useRef<{ file: File; id: string }[]>([])

  const form = useForm<DraftForm>({
    defaultValues: {
      description: "",
      sources: [],
      error: ""
    },
    resolver: yupResolver(schemaCommentDraft) as any
  })

  const sources = form.watch("sources")
  const error = form.watch("error")

  const { mutate, isPending } = useMutation({
    mutationFn: (data: { description: string; draftGalleries?: DraftGallery[] }) => {
      if (!data.draftGalleries?.length) {
        return updateDraft({
          content: data.description,
          draftId: draft?.id,
          removeDraftGalleryIds: refDeleted.current?.length ? refDeleted.current : undefined
        })
      }
      return uploadAssets(data.draftGalleries)
    },
    onSuccess(_, raw) {
      if (!raw?.draftGalleries?.length) {
        reset()
        toast.success("Cập nhật thành công")
        queryClient.invalidateQueries({
          queryKey: [`draft_details_${draft?.id}`]
        })
        refetch?.()
        return
      }
      updateDraft({
        content: raw.description,
        draftId: draft?.id,
        draftGalleries: raw.draftGalleries,
        removeDraftGalleryIds: refDeleted.current?.length ? refDeleted.current : undefined
      }).then(() => {
        reset()
        toast.success("Cập nhật thành công")
        queryClient.invalidateQueries({
          queryKey: [`draft_details_${draft?.id}`]
        })
        refetch?.()
      })
    },
    onError(error: any) {
      form.setValue("error", error?.message || "Có lỗi xảy ra!")
      toast.error(error?.message || "Có lỗi xảy ra!")
    }
  })

  const uploadAssets = async (draftGalleries: DraftGallery[]): Promise<any> => {
    if (!draft?.potentialId || !draft?.id) {
      throw { message: "Không tìm thấy định dạng bài đăng." }
    }

    // Get presigned URLs from API
    const urls = await getLinkS3UploadDraft(draftGalleries)
    const linkUploadGalleries = urls?.data?.data

    if (!linkUploadGalleries || !Array.isArray(linkUploadGalleries)) {
      throw { message: "Không thấy bản dữ liệu" }
    }

    // Map files with URLs
    const dataUploadS3 = linkUploadGalleries
      .map((url, index) => {
        const file = sourceRef.current[index]
        return { url, file: file?.file || null }
      })
      .filter((item) => !!item.file)

    // Upload all files with progress tracking
    await uploadMultipleFilesToS3(dataUploadS3, (progress) => {
      setProgress(progress)
    })

    return {
      description: form.getValues("description"),
      draftGalleries
    }
  }

  const onUpload = ({ description }: { description: string }) => {
    const videos = sourceRef.current
    if (!videos || !videos.length) {
      mutate({ description })
      return
    }

    const draftGalleries: DraftGallery[] = videos.map((item, index) => {
      const sanitizedName = sanitizeFileName(item.file.name)
      return {
        name: `${draft?.version}_${index}_${draft?.potentialId}_${sanitizedName}`,
        type: item.file.type.startsWith("image") ? EMediaType.IMAGE : EMediaType.VIDEO
      }
    })

    mutate({ description, draftGalleries })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const files = e.target.files
      if (files && files.length > 0) {
        const validFiles = Array.from(files).filter((file) => file.type.startsWith("video/"))
        const filesWithIds = validFiles.map((file) => ({
          file,
          id: generateUniqueId()
        }))
        sourceRef.current = [...sourceRef.current, ...filesWithIds]
        form.setValue("sources", sourceRef.current)
        form.setValue("error", "")
      } else {
        form.setValue("error", "Bản nháp phải là video")
      }
    } catch {
      form.setValue("error", "Bản nháp phải là video")
    }
  }

  const onRemove = (id: string) => {
    const currentList = (sources || []).filter((item) => item.id !== id)
    sourceRef.current = currentList
    form.setValue("sources", currentList)
  }

  const onRemoveExited = (id: string) => {
    const currentList = (sourceExited || []).filter((item) => item.id !== id)
    setSourceExited(currentList)
    if (id) {
      refDeleted.current = [...refDeleted.current, id]
    }
  }

  const handleCheck = () => {
    if (!draft?.id) {
      form.setValue("error", "Không tìm thấy đích bản nháp.")
      return
    }
    if ((!sources || !sources.length) && (!sourceExited || !sourceExited.length)) {
      form.setValue("error", "Lỗi chưa tải bản nháp.")
      return
    }
    setOpenConfirm(true)
  }

  const reset = () => {
    if (sourceRef.current) {
      sourceRef.current = []
    }
    if (refDeleted.current) {
      refDeleted.current = []
    }
    form.setValue("sources", [])
    setSourceExited([])
    setOpenUpload(false)
    setOpenConfirm(false)
    form.reset({})
  }

  const handleOpenUpload = () => {
    setOpenUpload(true)
    if (draft) {
      form.setValue("description", draft.content || "")
      setSourceExited(Array.isArray(draft.draftGalleries) ? draft.draftGalleries : [])
      sourceRef.current = []
      refDeleted.current = []
    }
  }

  return (
    <>
      <Button
        className="absolute top-0 h-8 right-0 text-xs bg-primary-800 text-white rounded-lg"
        onClick={handleOpenUpload}
      >
        Cập nhật
      </Button>

      <BottomSheetModal
        isVisible={openUpload}
        setIsVisible={(open) => {
          setOpenUpload(open)
          reset()
        }}
        title={`Cập nhật bản nháp ${draft?.version}`}
      >
        <div className={cn("flex flex-col gap-4 px-4")}>
          <Controller
            name="description"
            control={form.control}
            render={({ field, fieldState }) => (
              <MessageError error={fieldState.error?.message}>
                <p className="text-sm font-medium">Mô tả nội dung</p>
                <Textarea
                  {...field}
                  placeholder="Nhập mô tả..."
                  className="mt-2 text-sm rounded-xl"
                />
              </MessageError>
            )}
          />
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="border rounded-xl p-4">
            <div className="flex flex-wrap gap-4 justify-center">
              {sourceExited?.map((image: DraftGallery) => {
                const videoUrl = getImageByNameFromS3(
                  image.name,
                  image.createdBy || user?.id,
                  EFolderAssets.POTENTIAL_DRAFT
                )
                return (
                  <div
                    key={image.id}
                    className="relative w-32 h-32 border rounded-lg overflow-hidden"
                  >
                    <video
                      src={videoUrl}
                      className="object-cover w-full h-full"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const video = e.target as HTMLVideoElement
                        video.currentTime = 0.1
                      }}
                    />
                    <button
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      onClick={() => {
                        if (!isPending) {
                          onRemoveExited(image.id || "")
                        }
                      }}
                      disabled={isPending}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )
              })}
              {sources?.map((item) => {
                const videoUrl = URL.createObjectURL(item.file)
                return (
                  <div
                    key={item.id}
                    className="relative w-32 h-32 border rounded-lg overflow-hidden"
                  >
                    <video
                      src={videoUrl}
                      className="object-cover w-full h-full"
                      preload="metadata"
                      muted
                      playsInline
                      onLoadedMetadata={(e) => {
                        const video = e.target as HTMLVideoElement
                        video.currentTime = 0.1
                      }}
                    />
                    <button
                      className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full"
                      onClick={() => {
                        if (!isPending) {
                          URL.revokeObjectURL(videoUrl)
                          onRemove(item.id)
                        }
                      }}
                      disabled={isPending}
                    >
                      <X size={16} />
                    </button>
                  </div>
                )
              })}
            </div>
            {(!sources || !sources.length) && (!sourceExited || !sourceExited.length) && (
              <div className="flex justify-center mt-2">
                <label className="flex flex-col items-center justify-center w-32 h-32 rounded-xl border-2 border-dashed border-primary bg-gray-50 text-gray-500 cursor-pointer">
                  <Upload size={24} className="mb-2 text-primary" />
                  <p className="text-xs font-normal text-primary">Tải lên video</p>
                  <input
                    type="file"
                    accept="video/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isPending}
                  />
                </label>
              </div>
            )}
          </div>

          <Button
            className="w-full text-sm h-12 bg-primary-800 rounded-xl mb-2"
            disabled={
              isPending ||
              ((!sources || !sources.length) && (!sourceExited || !sourceExited.length))
            }
            onClick={form.handleSubmit(handleCheck)}
          >
            {isPending ? `Tải lên ${Math.floor(progress)}%` : "Cập nhật"}
          </Button>
        </div>
      </BottomSheetModal>

      <BottomSheetModal isVisible={openConfirm} setIsVisible={setOpenConfirm} title="Xác nhận">
        <div className={cn("flex flex-col gap-2 px-2")}>
          <p className="text-sm font-medium px-2">
            Thao tác này không thể chỉnh sửa. Hãy kiểm tra trước khi bấm xác nhận.
          </p>
          {isPending && (
            <div className="flex items-center justify-center">
              <p className="flex items-center gap-1 font-semibold text-sm">
                Đang tải lên
                <span className="text-sm font-semibold">{Math.floor(progress)}%</span>
              </p>
            </div>
          )}
          <Button
            className="w-full text-sm !h-12 rounded-xl bg-primary-800 mb-2"
            onClick={form.handleSubmit((data) => onUpload(data))}
            disabled={isPending}
          >
            Xác nhận tải lên
          </Button>
        </div>
      </BottomSheetModal>
    </>
  )
}
